import React, { useState, useEffect } from 'react';
import { CheckCircle, Shield, Camera, Upload, Loader2, X, Car } from 'lucide-react';
import { api } from '../../services/api';

export function DocumentUploadModal({ t, docStatus, onClose, onComplete, onDocStatusChange }) {
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [localUploaded, setLocalUploaded] = useState({});

  const PERSONAL_DOCS = [
    { key: 'selfie_with_license', label: t('driver_docs_selfie'), required: true },
    { key: 'transport_license', label: t('driver_docs_license'), required: false },
  ];

  const VEHICLE_DOCS = [
    { key: 'vehicle_registration', label: t('driver_docs_registration'), required: true },
    { key: 'vehicle_photo', label: t('driver_docs_vehicle_photo'), required: true },
    { key: 'insurance', label: t('driver_docs_insurance'), required: true },
  ];

  const handlePersonalDocUpload = async (docType, file) => {
    setUploadingDoc(docType);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await api.post(`/driver/upload-document/${docType}`, { document: reader.result });
        setLocalUploaded(prev => ({ ...prev, [docType]: true }));
        const res = await api.get('/driver/documents-status');
        onDocStatusChange(res.data);
      } catch (err) {
        console.error('Error uploading document:', err);
      } finally {
        setUploadingDoc(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleVehicleDocUpload = async (vehicleId, docType, file) => {
    const uploadKey = `${vehicleId}_${docType}`;
    setUploadingDoc(uploadKey);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await api.post(`/driver/vehicle/${vehicleId}/upload-document/${docType}`, { document: reader.result });
        setLocalUploaded(prev => ({ ...prev, [uploadKey]: true }));
        const res = await api.get('/driver/documents-status');
        onDocStatusChange(res.data);
      } catch (err) {
        console.error('Error uploading vehicle document:', err);
      } finally {
        setUploadingDoc(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileAction = (callback) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.onchange = (e) => { if (e.target.files[0]) callback(e.target.files[0]); };
    input.click();
  };

  const handleCameraAction = (callback) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = (e) => { if (e.target.files[0]) callback(e.target.files[0]); };
    input.click();
  };

  const handleComplete = async () => {
    const res = await api.get('/driver/documents-status');
    onDocStatusChange(res.data);
    if (res.data.complete) {
      onComplete(res.data);
    } else {
      alert(t('driver_docs_missing_alert'));
    }
  };

  const isPersonalUploaded = (key) => localUploaded[key] || docStatus?.personal_documents?.[key] || docStatus?.uploaded?.[key];

  const DocItem = ({ docKey, label, required, isUploaded, isUploading, onCamera, onUpload }) => (
    <div
      className={`p-4 rounded-xl border-2 transition-colors ${
        isUploaded
          ? 'border-emerald-200 bg-emerald-50'
          : required
          ? 'border-amber-200 bg-amber-50'
          : 'border-slate-200 bg-slate-50'
      }`}
      data-testid={`doc-item-${docKey}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isUploaded ? (
            <CheckCircle size={18} className="text-emerald-500" />
          ) : (
            <Shield size={18} className={required ? 'text-amber-500' : 'text-slate-400'} />
          )}
          <span className="font-medium text-slate-800 text-sm">{label}</span>
        </div>
        {isUploaded ? (
          <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">{t('driver_docs_uploaded')}</span>
        ) : (
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${required ? 'text-amber-600 bg-amber-100' : 'text-slate-500 bg-slate-200'}`}>
            {required ? t('driver_docs_required') : t('driver_docs_optional')}
          </span>
        )}
      </div>
      {isUploading ? (
        <div className="flex items-center justify-center py-2">
          <Loader2 size={20} className="animate-spin text-orange-500" />
        </div>
      ) : (
        <div className="flex gap-2">
          <button onClick={onCamera}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors"
            data-testid={`doc-camera-${docKey}`}>
            <Camera size={16} /> {t('driver_docs_camera')}
          </button>
          <button onClick={onUpload}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-100 transition-colors"
            data-testid={`doc-upload-${docKey}`}>
            <Upload size={16} /> {t('driver_docs_upload')}
          </button>
        </div>
      )}
    </div>
  );

  const vehiclesInfo = docStatus?.vehicles || [];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 sm:flex sm:items-center sm:justify-center sm:p-4" data-testid="doc-upload-modal">
      <div className="bg-white sm:rounded-2xl shadow-2xl sm:max-w-lg w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-800">{t('driver_docs_title')}</h2>
            <p className="text-sm text-slate-500 mt-1">{t('driver_docs_subtitle')}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-6">
          {/* Personal Documents */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Persönliche Dokumente</h3>
            <div className="space-y-3">
              {PERSONAL_DOCS.map((doc) => (
                <DocItem
                  key={doc.key}
                  docKey={doc.key}
                  label={doc.label}
                  required={doc.required}
                  isUploaded={isPersonalUploaded(doc.key)}
                  isUploading={uploadingDoc === doc.key}
                  onCamera={() => handleCameraAction((file) => handlePersonalDocUpload(doc.key, file))}
                  onUpload={() => handleFileAction((file) => handlePersonalDocUpload(doc.key, file))}
                />
              ))}
            </div>
          </div>

          {/* Vehicle Documents (per vehicle) */}
          {vehiclesInfo.map((vInfo) => (
            <div key={vInfo.vehicle_id}>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Car size={14} className="text-slate-400" />
                {vInfo.vehicle_name || 'Fahrzeug'}
                {vInfo.complete ? (
                  <span className="text-xs bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full">Vollständig</span>
                ) : (
                  <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">{vInfo.missing?.length} fehlt</span>
                )}
              </h3>
              <div className="space-y-3">
                {VEHICLE_DOCS.map((doc) => {
                  const uploadKey = `${vInfo.vehicle_id}_${doc.key}`;
                  const isMissing = vInfo.missing?.includes(doc.key);
                  const uploaded = localUploaded[uploadKey] || !isMissing;
                  return (
                    <DocItem
                      key={uploadKey}
                      docKey={uploadKey}
                      label={doc.label}
                      required={doc.required}
                      isUploaded={uploaded}
                      isUploading={uploadingDoc === uploadKey}
                      onCamera={() => handleCameraAction((file) => handleVehicleDocUpload(vInfo.vehicle_id, doc.key, file))}
                      onUpload={() => handleFileAction((file) => handleVehicleDocUpload(vInfo.vehicle_id, doc.key, file))}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="p-6 border-t border-slate-100">
          <button
            onClick={handleComplete}
            className="w-full py-3 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
            data-testid="doc-complete-btn"
          >
            <CheckCircle size={18} />
            {t('driver_docs_complete_btn')}
          </button>
        </div>
      </div>
    </div>
  );
}
